import { useLayout } from 'hooks/useLayout'
import { number, object, boolean } from 'yup'
import { useEffect, useCallback } from 'react'
import { Modal } from 'components/Modal/Modal'
import { useSnackbar } from 'stores/useSnackbar'
import { colorPrimitives } from 'components/Theme'
import { yupResolver } from '@hookform/resolvers/yup'
import { FormProvider, useForm } from 'react-hook-form'
import { TextField } from 'components/Shared/TextField'
import type { components } from 'api/playerPayback/api'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { isPresent } from '@jjvgaming/player-payback-library'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { ActivityButton } from 'components/ActivityButton'
import { ReferralPointsSwitch } from './ReferralPointsSwitch'

interface ReferralPointsSettingsForm {
  enableReferralPoints: boolean
  referralReferrerPoints: number
  referralRefereePoints: number
}

const ReferralPointsSettingsSchema = object({
  enableReferralPoints: boolean().required(
    'Enable Referral Points is required.'
  ),
  referralReferrerPoints: number()
    .typeError('Referrer Points value must be a number.')
    .test('is-positive', 'Value must be a positive number', (value) => {
      return typeof value === 'number' && value > 0
    })
    .required('Referrer Points value is required.'),
  referralRefereePoints: number()
    .typeError('Referee Points value must be a number.')
    .test('is-positive', 'Value must be a positive number', (value) => {
      return typeof value === 'number' && value > 0
    })
    .required('Referee Points value is required.'),
})

export const ReferralPointsSettingsModal = ({
  checkInPolicy,
  isModalVisible,
  toggleModalVisibility,
}: {
  checkInPolicy: components['schemas']['FullCheckInPolicyDTO']
  isModalVisible: boolean
  toggleModalVisibility: () => void
}) => {
  const { isMobile } = useLayout()
  const heightModal = isMobile ? '100%' : '75%'
  const setMessage = useSnackbar((state) => state.setMessage)
  const queryClient = useQueryClient()

  const formMethods = useForm<ReferralPointsSettingsForm>({
    resolver: yupResolver(ReferralPointsSettingsSchema),
  })

  const {
    handleSubmit,
    register,
    formState: { errors },
    watch,
    reset,
  } = formMethods

  const resetForm = useCallback(() => {
    reset({
      enableReferralPoints: Boolean(checkInPolicy?.enableReferralPoints),
      referralReferrerPoints: Number(checkInPolicy?.referralReferrerPoints),
      referralRefereePoints: Number(checkInPolicy?.referralRefereePoints),
    })
  }, [checkInPolicy])

  useEffect(() => {
    resetForm()
  }, [resetForm])

  const enableReferralPoints = watch('enableReferralPoints')

  const closeModal = () => {
    toggleModalVisibility()
    resetForm()
  }

  const putReferralPoints = useTypedApiClient({
    path: '/admin/policies/checkins/{id}',
    method: 'put',
  })

  const updateReferralPointsMutation = useMutation({
    mutationFn: async ({
      enableReferralPoints,
      referralReferrerPoints,
      referralRefereePoints,
    }: ReferralPointsSettingsForm) => {
      if (checkInPolicy?.id && checkInPolicy?.rewardsProgramId) {
        checkInPolicy.enableReferralPoints = enableReferralPoints
        checkInPolicy.referralReferrerPoints = enableReferralPoints
          ? referralReferrerPoints
          : 0
        checkInPolicy.referralRefereePoints = enableReferralPoints
          ? referralRefereePoints
          : 0
        return await putReferralPoints(checkInPolicy)
      }
    },
  })

  useEffect(() => {
    if (updateReferralPointsMutation.isSuccess) {
      queryClient
        .invalidateQueries({
          queryKey: [
            '/admin/policies/checkins/rewards-programs',
            checkInPolicy.rewardsProgramId,
          ],
        })
        .finally(() => {})
      setMessage('Referral Points have been updated.', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      closeModal()
    } else {
      const error: any = updateReferralPointsMutation.error as Error
      if (error?.message) {
        setMessage(error?.message, 'error', {
          vertical: 'top',
          horizontal: 'right',
        })
      }
    }
  }, [
    updateReferralPointsMutation.isSuccess,
    updateReferralPointsMutation.isError,
  ])

  const updateReferralPoints = handleSubmit((data) => {
    updateReferralPointsMutation.mutate(data)
  })

  return (
    <Modal
      isOpen={isModalVisible}
      toggleIsOpen={() => {}}
      sx={{
        width: isMobile ? '100%' : '70%',
        height: heightModal,
      }}
    >
      <FormProvider {...formMethods}>
        <form onSubmit={updateReferralPoints}>
          <ModalFullHeader
            title="Referral Points"
            subTitle={checkInPolicy.rewardsProgram?.name}
            toggleIsOpen={closeModal}
          />
          <ModalFullBody heightModal={heightModal}>
            <Stack spacing={5}>
              <ReferralPointsSwitch />
              {enableReferralPoints && (
                <>
                  <Box>
                    <Box className="flex flex-col sm:flex-row justify-stretch w-full px-1">
                      <Box className="w-full sm:w-1/2 sm:pt-4 flex">
                        <Typography>
                          # of Existing Patron Referrer Points
                        </Typography>
                        <Typography color={colorPrimitives.redGaming}>
                          *
                        </Typography>
                      </Box>
                      <Box className="flex flex-row gap-6 w-full sm:w-1/2">
                        <TextField
                          placeholder="Points"
                          {...register('referralReferrerPoints', {
                            required: true,
                          })}
                          error={isPresent(errors.referralReferrerPoints)}
                          sx={{ width: '100px' }}
                          bottomContent={
                            errors.referralReferrerPoints?.message && (
                              <FormFieldErrorMessage
                                message={errors.referralReferrerPoints.message}
                              />
                            )
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                  <Box>
                    <Box className="flex flex-col sm:flex-row justify-stretch w-full px-1">
                      <Box className="w-full sm:w-1/2 sm:pt-4 flex">
                        <Typography># of New Referee Points</Typography>
                        <Typography color={colorPrimitives.redGaming}>
                          *
                        </Typography>
                      </Box>
                      <Box className="flex flex-row gap-6 w-full sm:w-1/2">
                        <TextField
                          placeholder="Points"
                          {...register('referralRefereePoints', {
                            required: true,
                          })}
                          error={isPresent(errors.referralRefereePoints)}
                          sx={{ width: '100px' }}
                          bottomContent={
                            errors.referralRefereePoints?.message && (
                              <FormFieldErrorMessage
                                message={errors.referralRefereePoints.message}
                              />
                            )
                          }
                        />
                      </Box>
                    </Box>
                  </Box>
                </>
              )}
            </Stack>
          </ModalFullBody>
          <ModalFullFooter>
            <Button variant="text" onClick={closeModal}>
              Cancel
            </Button>
            <ActivityButton
              active={updateReferralPointsMutation.isPending}
              variant="contained"
              type="submit"
            >
              Save
            </ActivityButton>
          </ModalFullFooter>
        </form>
      </FormProvider>
    </Modal>
  )
}
